import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  ArrowLeft,
  ArrowUpRight,
  ExternalLink,
  Calendar,
  Download,
  QrCode,
  ShieldCheck,
  BellRing,
  FlaskConical,
  Sparkles,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { useLang } from "@/i18n";
import { usePageTitle } from "@/lib/useTitle";
import { SiteHeader } from "@/components/SiteHeader";
import { SubscribeForm } from "@/components/SubscribeForm";
import { TagPill } from "@/lib/colors";
import { ImageGallery } from "@/components/ImageGallery";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function BatchPage() {
  const { t } = useLang();
  const { code } = useParams();
  const [batch, setBatch] = useState(null);
  const [status, setStatus] = useState("loading");
  const qrRef = useRef(null);

  usePageTitle(
    batch
      ? `${batch.product_name} (${t("title.batchLabel")}: ${batch.batch_code}) | ${t("title.brand")}`
      : t("title.brand")
  );

  useEffect(() => {
    setStatus("loading");
    api.get(`/batches/${code}`).then((res) => { setBatch(res.data); setStatus("ok"); }).catch(() => setStatus("notfound"));
  }, [code]);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code}-qr.png`;
    a.click();
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16"><div className="h-72 rounded-sm bg-secondary animate-pulse" /></div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-28 text-center" data-testid="batch-not-found">
          <span className="font-data text-xs uppercase tracking-[0.2em] text-sage-dark font-semibold">{code}</span>
          <h1 className="font-heading text-4xl sm:text-5xl text-ink mt-3">{t("batch.notfound.title")}</h1>
          <p className="mt-4 text-gray-600">{t("batch.notfound.text")}</p>
          <Link to="/archiv" data-testid="back-home-link">
            <Button className="mt-8 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
              <ArrowLeft className="h-4 w-4" /> {t("batch.notfound.back")}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const pageUrl = `${window.location.origin}/batch/${batch.batch_code}`;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader
        right={
          <Link to="/archiv" data-testid="back-archive-link" className="flex items-center gap-2 text-sm text-gray-500 hover:text-ink transition-colors duration-200 font-semibold">
            <ArrowLeft className="h-4 w-4" /> {t("batch.back")}
          </Link>
        }
      />

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="animate-fade-up">
            <ImageGallery images={batch.images} alt={batch.product_name} testid="batch-gallery" />
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "80ms" }}>
            {(() => {
              const allSourced = batch.ingredients.length > 0 && batch.ingredients.every((i) => i.supplier_url && i.supplier_url.trim());
              const hasExceeded = batch.check_dosage_calculation && batch.ingredients.some((i) => i.dosage_status === "exceeded");
              const isVerified = batch.is_certified && allSourced && !hasExceeded;
              return isVerified ? (
                <div data-testid="status-badge-verified" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sage/15 text-sage-dark font-data text-xs uppercase tracking-[0.15em] font-bold">
                  <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.5} /> {t("batch.verified")}
                </div>
              ) : (
                <div data-testid="status-badge-uncertified" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gray-100 text-gray-500 font-data text-xs uppercase tracking-[0.15em] font-bold">
                  <FlaskConical className="h-3.5 w-3.5" strokeWidth={2.5} /> {t("batch.cert.uncertified")}
                </div>
              );
            })()}
            <span className="block font-data text-sm uppercase tracking-[0.18em] text-sage-dark mt-5 font-semibold" data-testid="batch-code-label">{batch.batch_code}</span>
            <h1 className="font-heading text-3xl sm:text-5xl text-ink leading-[1.05] mt-2" data-testid="batch-product-name">{batch.product_name}</h1>
            <div className="mt-4 flex items-center gap-2 text-gray-600 font-data text-sm">
              <Calendar className="h-4 w-4" strokeWidth={2} />
              <span data-testid="batch-date">{t("batch.produced")} {formatDate(batch.production_date)}</span>
            </div>

            {batch.inherited_tags && batch.inherited_tags.length > 0 && (
              <div className="mt-6" data-testid="inherited-tags">
                <p className="font-data text-xs uppercase tracking-[0.15em] text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> {t("batch.inheritedTags")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {batch.inherited_tags.map((tag) => (
                    <TagPill key={tag.id} tag={tag} testid={`inherited-tag-${tag.id}`} />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center gap-5 p-5 border border-border rounded-sm bg-secondary/40">
              <div ref={qrRef} className="bg-white p-2 rounded-sm border border-border" data-testid="batch-qr">
                <QRCodeCanvas value={pageUrl} size={104} fgColor="#1C1C1C" bgColor="#FFFFFF" level="M" />
              </div>
              <div className="flex-1">
                <p className="font-data text-xs uppercase tracking-[0.15em] text-gray-500 flex items-center gap-1.5 font-semibold">
                  <QrCode className="h-3.5 w-3.5" /> {t("batch.qr.scan")}
                </p>
                <p className="text-sm text-gray-700 mt-1 leading-snug">{t("batch.qr.print")}</p>
                <Button onClick={downloadQR} data-testid="download-qr-button" variant="outline" className="mt-3 h-9 rounded-sm border-ink/20 hover:bg-ink hover:text-white gap-2 text-sm font-semibold">
                  <Download className="h-4 w-4" /> {t("batch.qr.download")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* INCI table */}
        <div className="mt-14 sm:mt-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <span className="font-data text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">{t("batch.inci.kicker")}</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-ink mt-1">{t("batch.inci.title")}</h2>
            </div>
            <span className="font-data text-sm text-gray-400">{batch.ingredients.length} {t("batch.inci.listed")}</span>
          </div>

          {batch.check_dosage_calculation && batch.ingredients.some((i) => i.dosage_status === "exceeded") && (
            <div data-testid="dosage-red-alert" className="mb-6 flex gap-3 items-start p-4 rounded-sm border-2 border-red-300 bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="text-sm font-bold text-red-800">{t("dosage.exceeded.box")}</p>
                <p className="text-sm text-red-700 mt-1">
                  {t("dosage.exceeded.detail")}{" "}
                  {batch.ingredients.filter((i) => i.dosage_status === "exceeded").map((i) => i.name).join(", ")}.
                </p>
              </div>
            </div>
          )}

          <div className="border border-border rounded-sm overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-secondary border-b border-border">
              <span className="col-span-6 font-data text-xs uppercase tracking-[0.15em] text-gray-600 font-semibold">{t("batch.table.ingredient")}</span>
              <span className="col-span-3 font-data text-xs uppercase tracking-[0.15em] text-gray-600 font-semibold">{t("id.tags")}</span>
              <span className="col-span-3 font-data text-xs uppercase tracking-[0.15em] text-gray-600 font-semibold text-right">{t("batch.table.supplier")}</span>
            </div>

            {batch.ingredients.map((ing, idx) => (
              <div key={ing.id} data-testid={`ingredient-row-${idx}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors duration-200 items-center">
                <div className="sm:col-span-6">
                  <Link to={`/ingredient/${ing.id}`} data-testid={`ingredient-link-${idx}`} className="group inline-flex items-center gap-1.5 font-data text-sm font-semibold text-ink hover:text-sage-dark transition-colors duration-200">
                    {ing.name}
                    <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-sage-dark transition-colors duration-200" />
                  </Link>
                  {ing.category && (<span className="block text-xs text-gray-400 mt-0.5">{ing.category}</span>)}
                  <span className="block text-xs font-semibold text-sage-dark mt-0.5" data-testid={`ingredient-percent-${idx}`}>
                    {t("batch.content.label")}: {ing.percentage != null ? `${ing.percentage} %` : t("batch.percent.unknown")}
                  </span>
                  {batch.check_dosage_calculation && ing.dosage_status === "exceeded" && (
                    <span data-testid={`dosage-badge-${idx}`} className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold font-data">
                      <AlertTriangle className="h-3 w-3" /> {t("dosage.exceeded.short")}
                      {ing.max_recommended_dosage_percent != null ? ` (max ${ing.max_recommended_dosage_percent} %)` : ""}
                    </span>
                  )}
                  {batch.check_dosage_calculation && ing.dosage_status === "undefined" && (
                    <span data-testid={`dosage-badge-${idx}`} className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold font-data">
                      <AlertCircle className="h-3 w-3" /> {t("dosage.undefined")}
                    </span>
                  )}
                </div>
                <div className="sm:col-span-3 flex flex-wrap gap-1.5">
                  {ing.tags && ing.tags.length > 0 ? (
                    ing.tags.map((tg) => (<TagPill key={tg.id} tag={tg} testid={`ingredient-tag-${idx}-${tg.id}`} />))
                  ) : (<span className="text-gray-300 text-xs font-data">—</span>)}
                </div>
                <div className="sm:col-span-3 sm:text-right">
                  {ing.supplier_url ? (
                    <a href={ing.supplier_url} target="_blank" rel="noopener noreferrer" data-testid={`verify-supplier-button-${idx}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-ink/15 text-sm font-semibold text-ink hover:bg-ink hover:text-white transition-colors duration-200">
                      {t("common.verify")} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (<span className="text-gray-300 text-xs font-data">{t("batch.noSource")}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Batch-specific subscription */}
        <div className="mt-14 sm:mt-20">
          <div className="max-w-2xl">
            <SubscribeForm
              testid="batch-subscribe"
              accent="amber"
              icon={BellRing}
              endpoint="/subscribe/batch"
              extra={{ batch_code: batch.batch_code }}
              title={t("batchsub.title")}
              description={t("batchsub.desc")}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
